import { Injectable } from '@angular/core';
import { LoggingService, LogSeverity } from '@core/services/logging.service';

/**
 * Represents a registry of sanity checks to monitor the application
 *
 * A check succeeds when `run()` resolves to `true`.
 * Any `false` result (or thrown / rejected error) is treated as a failure
 * and routed through the {@link LoggingService}.
 */
export interface SanityCheck {
  /** Unique identifier for this check (appears in log metadata). */
  id: string;
  /** Human-readable description shown in log messages. */
  description: string;
  /**
   * Validation callback.
   *
   * @returns `true` when the condition is satisfied, `false` (or a rejected Promise)
   *          when the check fails.
   */
  run(): boolean | Promise<boolean>;
  /** Optional helper returning the value the check expected. */
  expected?(): any;
  /** Optional helper returning the value the check actually observed. */
  actual?(): any;
  /** Optional additional metadata specific to this check. */
  detail?(): any;
  /**
   * Log level to use when the check fails.
   * Defaults to {@link LogSeverity.INFO} when omitted.
   */
  severity?: LogSeverity;
  /**
   * Optional callback executed **after** a failure is logged.
   * Useful for self-healing actions (e.g. reload, clear cache, prompt user).
   */
  onFail?(chk: SanityCheck): void | Promise<void>;
}

/**
 * Service that executes registered {@link SanityCheck}s and logs failures.
 *
 * Typical usage:
 * ```ts
 * // app.module.ts
 * providers: [
 *   {
 *     provide: APP_INITIALIZER,
 *     multi: true,
 *     deps: [SanityCheckService],
 *     useFactory: (svc: SanityCheckService) => () => svc.runAll(),
 *   },
 * ]
 * ```
 */
@Injectable({ providedIn: 'root' })
export class SanityCheckService {
  private readonly checks: SanityCheck[] = [];

  /**
   * @param logger Shared application logger used to record failures.
   */
  constructor(private logger: LoggingService) {
    this.registerBuiltIns();
  }

  /* ------------------------------------------------------------------ */
  /*  Public API                                                         */
  /* ------------------------------------------------------------------ */

  /**
   * Registers a new sanity check.
   *
   * Call this from feature modules or services that require their own health
   * validation.
   *
   * @param check The {@link SanityCheck} instance to add.
   */
  registerCheck(check: SanityCheck): void {
    this.checks.push(check);
  }

  /**
   * Executes every registered check once and logs any failures.
   *
   * Intended to be invoked during application bootstrap
   * (e.g. via `APP_INITIALIZER`) or manually for diagnostics.
   */
  async runAll(): Promise<void> {
    for (const chk of this.checks) {
      try {
        const ok = await Promise.resolve(chk.run());
        if (!ok) this.logFailure(chk);
      } catch (err) {
        this.logFailure(chk, { error: err });
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Internal helpers                                                   */
  /* ------------------------------------------------------------------ */

  /**
   * Logs a failed check through the shared {@link LoggingService}.
   *
   * @param chk   The failed {@link SanityCheck}.
   * @param extra Additional metadata to merge into the log message.
   * @internal
   */
  private logFailure(chk: SanityCheck, extra: any = {}): void {
    const severity = chk.severity ?? LogSeverity.INFO;
    this.logger.writeLog('[SanityCheck] ' + chk.description, {
      detail: {
        id: chk.id,
        expected: chk.expected?.(),
        actual: chk.actual?.(),
        ...extra,
      },
      severity,
    });
  }

  /**
   * Seeds the service with built-in checks that run for every build.
   *
   * Extend or modify this list at will to check the health of FreshAi
   * @internal
   */
  private registerBuiltIns(): void {
    /* Screen-size check, just in case the DMB misbehaves */
    const expectedSize = { w: 1080, h: 1920 };

    this.registerCheck({
      id: 'screen-size',
      description: 'Unexpected screen dimensions',
      run: () => window.innerWidth === expectedSize.w && window.innerHeight === expectedSize.h,
      expected: () => expectedSize,
      actual: () => ({ w: window.innerWidth, h: window.innerHeight }),
      severity: LogSeverity.WARNING,
    });
  }
}
